import React, { useEffect, useRef, useState } from 'react';
import { useOrion } from '../context/OrionContext';
import Icon from './Icon';

// ─────────────────────────────────────────────────────────────────────────────
// SINO — o mesmo do Nexus: clicar abre um painel suspenso com as notificações
// mais recentes, e clicar numa delas abre o ticket e marca como lida de uma
// vez. O rodapé leva à caixa de entrada completa (views/Notificacoes.js), com
// o histórico inteiro e o filtro de não lidas.
// ─────────────────────────────────────────────────────────────────────────────

/** Quantas o painel mostra. O resto está na caixa de entrada. */
const NO_PAINEL = 15;

/** Ícone e cor por tipo. O que não está aqui é aviso genérico. */
export const TIPOS_NOTIFICACAO = {
  retorno_sistemas: { icone: 'corner-up-left', classe: 'ntf-t-retorno' },
  pulo_apurado: { icone: 'shield-check', classe: 'ntf-t-pulo' },
  ticket: { icone: 'ticket', classe: 'ntf-t-ticket' },
};
export const TIPO_PADRAO = { icone: 'bell', classe: 'ntf-t-aviso' };

/** "agora", "5 min", "14:20" (hoje), "12/10 09:15". */
export function quandoNotificacao(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const agora = new Date();
  const minutos = Math.floor((agora - d) / 60000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `${minutos} min`;
  const pad = (n) => String(n).padStart(2, '0');
  if (d.toDateString() === agora.toDateString()) return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function SinoNotificacoes() {
  const { notificacoes, abrirNotificacao, setView } = useOrion();
  const { itens = [], naoLidas = 0, situacao, marcarTodas } = notificacoes || {};
  const [aberto, setAberto] = useState(false);
  const caixa = useRef(null);

  // Fechar ao clicar fora e no Esc, como todo painel suspenso.
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => { if (caixa.current && !caixa.current.contains(e.target)) setAberto(false); };
    const esc = (e) => { if (e.key === 'Escape') setAberto(false); };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);

  const abrir = (n) => { setAberto(false); abrirNotificacao(n); };
  const verCaixa = () => { setAberto(false); setView('notificacoes'); };

  return (
    <div className="notif no-drag" ref={caixa}>
      <button
        type="button"
        className={`icon-btn notif-btn ${aberto ? 'aberto' : ''}`}
        onClick={() => setAberto(v => !v)}
        title={naoLidas ? `${naoLidas} notificaç${naoLidas === 1 ? 'ão' : 'ões'} não lida${naoLidas === 1 ? '' : 's'}` : 'Notificações'}
        aria-label="Notificações"
        aria-expanded={aberto}
      >
        <Icon name="bell" size={17} />
        {naoLidas > 0 && <span className="notif-badge">{naoLidas > 99 ? '99+' : naoLidas}</span>}
      </button>

      {aberto && (
        <div className="notif-painel">
          <div className="notif-topo">
            <span className="notif-tit">Notificações</span>
            {naoLidas > 0 && (
              <button type="button" className="notif-limpar" onClick={marcarTodas}>
                marcar todas como lidas
              </button>
            )}
          </div>

          <div className="notif-lista">
            {situacao === 'sem-tabela' ? (
              <div className="notif-vazio">
                As notificações ainda não estão ativas: falta rodar
                {' '}<code>criar-tabela_notificacao_20261029_1800.sql</code> no Supabase.
              </div>
            ) : !itens.length ? (
              <div className="notif-vazio">
                Nada por aqui. Você é avisado quando um ticket seu voltar do
                {' '}<b>setor de Sistemas</b> ou quando a gerência apurar um <b>pulo de fila</b> seu.
              </div>
            ) : itens.slice(0, NO_PAINEL).map((n) => {
              const t = TIPOS_NOTIFICACAO[n.tipo] || TIPO_PADRAO;
              return (
                <button
                  type="button"
                  key={n.id}
                  className={`notif-item ${t.classe} ${n.lida_em ? '' : 'nao-lida'}`}
                  onClick={() => abrir(n)}
                >
                  <span className="notif-ic"><Icon name={t.icone} size={13} /></span>
                  <span className="notif-corpo">
                    <span className="notif-linha">
                      <span className="notif-titulo">{n.titulo}</span>
                      <span className="notif-quando">{quandoNotificacao(n.criada_em)}</span>
                    </span>
                    {!!n.texto && <span className="notif-texto">{n.texto}</span>}
                    <span className="notif-meta">
                      {n.origem}
                      {n.origem && n.numero_ticket && <> · </>}
                      {n.numero_ticket && <>ticket {n.numero_ticket}</>}
                    </span>
                  </span>
                  {!n.lida_em && <span className="notif-ponto" />}
                </button>
              );
            })}
          </div>

          <button type="button" className="notif-rodape" onClick={verCaixa}>
            <Icon name="inbox" size={13} /> Ver caixa de entrada
            {itens.length > NO_PAINEL && <span className="notif-rodape-mais">+{itens.length - NO_PAINEL}</span>}
          </button>
        </div>
      )}
    </div>
  );
}
